/**
 * Supplier SCAR module hooks (C-8). Backed by /api/v1/scars (real Postgres,
 * supplier.scars). A SCAR (Supplier Corrective Action Request) is the formal
 * record issued to a supplier; it links to deviations / complaints / audit
 * findings via the C-4 event graph (RelatedRecords on the detail screen).
 */
import {
  useMutation, useQuery, useQueryClient,
  type UseMutationResult, type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export interface ScarHistoryRow {
  event_kind: string; from_status: string | null; to_status: string | null;
  note: string | null; at_utc: string; actor_name: string | null;
}
export interface Scar {
  scarId: string;
  scarNumber: string;
  supplierId: string | null;
  supplierName: string | null;
  title: string;
  description: string | null;
  source: string;
  severity: string;
  status: string;
  supplierResponse: string | null;
  rootCause: string | null;
  correctiveAction: string | null;
  preventiveAction: string | null;
  issuedDate: string | null;
  responseDueDate: string | null;
  responseReceivedDate: string | null;
  ownerName: string | null;
  openedAt: string;
  closedAt: string | null;
  history?: ScarHistoryRow[];
}
export interface ScarStats { total: number; open: number; critical_open: number; overdue: number; closed: number }
export interface ScarListResp { items: Scar[]; total: number; stats: ScarStats }

export interface ScarFilters { status?: string; severity?: string; supplierId?: string }

export function useScarsQuery(filters: ScarFilters = {}): UseQueryResult<ScarListResp> {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.severity) qs.set('severity', filters.severity);
  if (filters.supplierId) qs.set('supplierId', filters.supplierId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<ScarListResp>({
    queryKey: ['scars', filters],
    queryFn: ({ signal }) => apiFetch<ScarListResp>(`/api/v1/scars${suffix}`, { signal }),
    staleTime: 30_000,
  });
}
export function useScarDetailQuery(id: string | undefined): UseQueryResult<Scar> {
  return useQuery<Scar>({
    queryKey: ['scars', 'detail', id],
    queryFn: ({ signal }) => apiFetch<Scar>(`/api/v1/scars/${encodeURIComponent(id!)}`, { signal }),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export interface NewScarInput {
  supplierId: string; title: string; description?: string; source?: string; severity?: string; responseDueDate?: string;
}
export function useCreateScar(): UseMutationResult<Scar, Error, NewScarInput> {
  const qc = useQueryClient();
  return useMutation<Scar, Error, NewScarInput>({
    mutationFn: (body) => apiFetch<Scar>('/api/v1/scars', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['scars'] }),
  });
}
export interface ScarResponseInput { supplierResponse?: string; rootCause?: string; correctiveAction?: string; preventiveAction?: string }
export function useScarRespond(id: string | undefined): UseMutationResult<Scar, Error, ScarResponseInput> {
  const qc = useQueryClient();
  return useMutation<Scar, Error, ScarResponseInput>({
    mutationFn: (body) => apiFetch<Scar>(`/api/v1/scars/${encodeURIComponent(id!)}/respond`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['scars'] }),
  });
}
export function useScarTransition(id: string | undefined): UseMutationResult<unknown, Error, { toStatus: string; note?: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { toStatus: string; note?: string }) =>
      apiFetch(`/api/v1/scars/${encodeURIComponent(id!)}/transition`, { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['scars'] }),
  });
}

// Lightweight supplier list for the "Issue SCAR" picker.
export interface SupplierPick { id: string; name: string; criticality: string }
export function useSupplierPickerQuery(): UseQueryResult<SupplierPick[]> {
  return useQuery<SupplierPick[]>({
    queryKey: ['scars', 'supplier-picker'],
    queryFn: async ({ signal }) => {
      const r = await apiFetch<{ items: Array<{ id: string; name: string; criticality: string }> }>('/api/v1/suppliers', { signal });
      return (r.items ?? []).map((s) => ({ id: s.id, name: s.name, criticality: s.criticality }));
    },
    staleTime: 60_000,
  });
}

// Mirror of the server FSM.
export const SCAR_FSM: Record<string, string[]> = {
  ISSUED: ['SUPPLIER_RESPONSE', 'CANCELLED'],
  SUPPLIER_RESPONSE: ['UNDER_REVIEW', 'CANCELLED'],
  UNDER_REVIEW: ['ACCEPTED', 'REJECTED'],
  REJECTED: ['SUPPLIER_RESPONSE', 'CANCELLED'],
  ACCEPTED: ['CLOSED'],
  CLOSED: [],
  CANCELLED: [],
};
export const SCAR_STATUS_LABEL: Record<string, string> = {
  ISSUED: 'Issued', SUPPLIER_RESPONSE: 'Supplier response', UNDER_REVIEW: 'Under review',
  ACCEPTED: 'Accepted', REJECTED: 'Rejected', CLOSED: 'Closed', CANCELLED: 'Cancelled',
};
export const SCAR_SOURCE_LABEL: Record<string, string> = {
  AUDIT_FINDING: 'Audit finding', COMPLAINT: 'Complaint', DEVIATION: 'Deviation',
  SCN: 'Change notification', INCOMING_INSPECTION: 'Incoming inspection', OTHER: 'Other',
};
export const SCAR_SEVERITY_LABEL: Record<string, string> = { CRITICAL: 'Critical', MAJOR: 'Major', MINOR: 'Minor' };
